/**
 * Payors Directory (US-028.1).
 *
 * Lists payors visible to the signed-in athlete. Each row shows a
 * ValidationBadge so the athlete sees the KYB state at a glance.
 * "Add payor" routes to the detail page in create mode (deep link with
 * `?new=1`); editing happens on the detail page too.
 */

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  ValidationBadge,
  type ValidationBadgeStatus,
} from '@app/ui';
import { PageTransition } from '@app/ui/motion';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../../components/route-error';
import { api } from '../../../lib/api';

const searchSchema = z.object({
  search: z.string().optional(),
  page: z.number().int().positive().default(1),
});

export const Route = createFileRoute('/_authenticated/payors/')({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ search: search.search, page: search.page }),
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.payor.list.queryOptions({
        input: { search: deps.search, page: deps.page, limit: 50 },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: PayorsDirectory,
});

function PayorsDirectory() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [searchInput, setSearchInput] = useState(search.search ?? '');

  // Debounce search updates into the URL — matches the brands directory.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((search.search ?? '') !== searchInput) {
        navigate({
          search: (prev) => ({
            ...prev,
            search: searchInput || undefined,
            page: 1,
          }),
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search.search, navigate]);

  const { data } = useSuspenseQuery(
    api.payor.list.queryOptions({
      input: { search: search.search, page: search.page, limit: 50 },
    }),
  );

  return (
    <PageTransition>
      <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Payors</h1>
            <p className="text-muted-foreground mt-1">
              The businesses or people who pay you. Add each one once — reuse them on every deal.
            </p>
          </div>
          <Link to="/payors/$payorId" params={{ payorId: 'new' }}>
            <Button>+ Add payor</Button>
          </Link>
        </div>

        <div className="mb-4">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, contact, or email…"
          />
        </div>

        {data.payors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No payors yet.
              <div className="mt-3">
                <Link to="/payors/$payorId" params={{ payorId: 'new' }}>
                  <Button variant="outline">Add your first payor</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {data.payors.map((payor: PayorRow) => (
              <Link
                key={payor.id}
                to="/payors/$payorId"
                params={{ payorId: payor.id }}
                className="block"
              >
                <Card className="hover:bg-accent/40 transition">
                  <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{payor.legalName}</CardTitle>
                      {payor.displayName && payor.displayName !== payor.legalName && (
                        <CardDescription className="truncate">
                          d/b/a {payor.displayName}
                        </CardDescription>
                      )}
                    </div>
                    <ValidationBadge status={payor.validationStatus as ValidationBadgeStatus} />
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground pt-0">
                    {[payor.contactName, payor.contactEmail, payor.contactPhone]
                      .filter(Boolean)
                      .join(' · ') || 'No contact info yet'}
                    <span className="block mt-1 text-xs">
                      Affiliation:{' '}
                      <span className="font-medium">{formatAffiliation(payor.affiliation)}</span>
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
}

function formatAffiliation(a: string): string {
  if (a === 'associated') return 'Associated (school-affiliated)';
  if (a === 'non_associated') return 'Independent';
  return 'Not sure';
}

interface PayorRow {
  id: string;
  legalName: string;
  displayName?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  affiliation: string;
  validationStatus: string;
}
